import React, { Fragment, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import {
  Flex,
  AssetType,
  PopupMenu,
  Text,
  Button,
  AnyObject,
  ErrorCode,
  SpacePermission,
  useClipboard,
  Asset,
} from "@thenounproject/lingo-core";

import CustomExportOption from "./CustomExportOption";
import PresetExportOption from "./PresetExportOption";
import { Inspectable } from "@constants/Inspector";
import useGenerateFilecut from "@actions/useGenerateFilecut";
import useNotifications from "@actions/useNotifications";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useDownloadFile from "@actions/useDownloadFile";
import useNavPoint from "@hooks/useNavPoint";

const ExportMenu = styled(PopupMenu).attrs({
  source: "inspector-asset-download",
  p: "m",
  width: 287,
})``;

const ExportTitle = styled(Flex).attrs({
  mb: "s",
  justifyContent: "space-between",
})``;

const SketchTitle = styled(Text).attrs({
  color: "grayDark",
  mb: "s",
  font: "ui.small",
})``;

export type IconStatus = "loading" | "success" | "error" | null;

export type CopyStatuses = {
  preset: { fileType: string; status: IconStatus };
  custom: { fileType: string; status: IconStatus };
};

export type DownloadAsset = (
  source: "preset" | "custom",
  type: string,
  size: string,
  dpi?: number,
  e?: React.MouseEvent
) => void;

export type CopyAsset = (
  source: "preset" | "custom",
  type: string,
  size: string,
  asset: Asset,
  dpi?: number,
  e?: React.MouseEvent
) => void;

export type AssetDownloadPopupRenderProps = {
  canManageCuts: boolean;
  noCuts: boolean;
  buttonProps: AnyObject & { onClick: (e: React.MouseEvent) => void };
};

type DownloadStatuses = {
  preset: { fileType: string; status: Exclude<IconStatus, null> } | null;
  custom: { fileType: string; status: Exclude<IconStatus, null> } | null;
};

type Props = {
  inspectable: Inspectable;
  canEdit?: boolean;
  popupVPos: string;
  popupHPos: string;
  children?: (renderProps: AssetDownloadPopupRenderProps) => React.ReactNode;
};

const AssetDownloadPopup: React.FC<Props> = ({
  inspectable,
  canEdit = false,
  popupVPos,
  popupHPos,
  children,
}) => {
  const canManageCuts = canEdit;
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [assetCopyData, setAssetCopyData] = useState(null);
  const { asset } = inspectable;
  const noCuts = !asset.meta.filecuts;
  const [generateFilecut] = useGenerateFilecut();
  const { showNotification } = useNotifications();
  const { showModal, dismissModal } = useShowModal();
  const downloadFile = useDownloadFile();
  const { space, portalId } = useNavPoint();
  const { onCopy, hasCopied } = useClipboard(assetCopyData);
  const [fileBeingCopied, setFileBeingCopied] = useState(null);
  const [copySource, setCopySource] = useState<"preset" | "custom" | null>(null);
  const [copyStatus, setCopyStatus] = useState<CopyStatuses>({ preset: null, custom: null });
  const [downloadStatuses, setDownloadStatuses] = useState<DownloadStatuses>({
    preset: null,
    custom: null,
  });

  const isSafari = React.useMemo(
    () =>
      window.navigator.userAgent.includes("Safari") &&
      !window.navigator.userAgent.includes("Chrome"),
    []
  );

  const withTimeout = (callback: () => void, duration: number) => {
    const timer = setTimeout(callback, duration);
    return () => clearTimeout(timer);
  };

  useEffect(() => {
    if (assetCopyData) {
      onCopy();
    }
  }, [assetCopyData, onCopy]);

  useEffect(() => {
    if (!hasCopied) return;

    showNotification({
      message: `${fileBeingCopied} has been copied to your clipboard`,
      level: "info",
    });

    setCopyStatus(prev => ({
      ...prev,
      [copySource]: { fileType: fileBeingCopied.slice(-3), status: "success" },
    }));

    return withTimeout(
      () =>
        setCopyStatus(prev => ({
          ...prev,
          [copySource]: { fileType: fileBeingCopied.slice(-3), status: null },
        })),
      3000
    );
  }, [hasCopied, showNotification, fileBeingCopied, copySource]);

  useEffect(() => {
    ["preset", "custom"].forEach(source => {
      const current: { fileType: string; status: IconStatus } = downloadStatuses[source];
      if (current && (current.status === "success" || current.status === "error")) {
        return withTimeout(() => setDownloadStatuses(prev => ({ ...prev, [source]: null })), 3000);
      }
    });
  }, [downloadStatuses]);

  const downloadAsset: DownloadAsset = useCallback(
    async (
      source: "preset" | "custom",
      fileType: string,
      dimensions: string,
      dpi?: number,
      e?: React.MouseEvent
    ) => {
      if (e) e.preventDefault();

      const assetName = asset.name || asset.meta.font?.displayName || "Unnamed asset";

      const useLibrary =
        space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true;

      showNotification({
        message: `Preparing ${assetName} ${fileType}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} for download...`,
      });

      setDownloadStatuses(prev => ({
        ...prev,
        [source]: { fileType, status: "loading" },
      }));

      const action = await generateFilecut({
        inspectable,
        fileType,
        dimensions,
        dpi,
        portalId,
        isAdmin: useLibrary,
      });
      if (action.error) {
        const { message } = action.error || {};
        setDownloadStatuses(prev => ({
          ...prev,
          [source]: { fileType, status: "error" },
        }));

        if (action.error.code === ErrorCode.downloadRequiresIdentity) {
          return showModal(ModalTypes.INFO_PROMPT, {
            callback: () => downloadAsset(source, fileType, dimensions, dpi, e),
          });
        }
        if (action.error.code === ErrorCode.downloadRequestPending) {
          return showModal(ModalTypes.CONFIRMATION, {
            title: "Request Pending",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
        }
        if (action.error.code === ErrorCode.downloadRequiresVerifiedIdentity) {
          return showModal(ModalTypes.CONFIRMATION, {
            title: "Request Approved",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
        }
        if (action.error.code === ErrorCode.downloadRequestDenied) {
          return showModal(ModalTypes.CONFIRMATION, {
            title: "Request Denied",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
        }
        showNotification({
          message:
            message || "There was a problem preparing your file for download. Please try again.",
          level: "error",
          link: {
            text: "View asset.",
            url: asset.permalink,
          },
        });
      } else {
        showNotification({ message: "Your download should begin in just a second" });
        downloadFile(action.response.result.url);
        setDownloadStatuses(prev => ({
          ...prev,
          [source]: { fileType, status: "success" },
        }));
      }
    },
    [
      asset.name,
      asset.meta.font?.displayName,
      asset.permalink,
      space?.access?.permissions,
      generateFilecut,
      inspectable,
      portalId,
      showNotification,
      showModal,
      dismissModal,
      downloadFile,
    ]
  );

  const copyAsset: CopyAsset = useCallback(
    async (
      source: "preset" | "custom",
      fileType: string,
      dimensions: string,
      asset: Asset,
      dpi?: number,
      e?: React.MouseEvent
    ) => {
      if (e) e.preventDefault();
      setCopySource(source);
      setCopyStatus(prev => ({ ...prev, [source]: { fileType, status: "loading" } }));

      // Store the current event for Safari
      const originalEvent = e;

      try {
        const useLibrary =
          space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true;

        showNotification({
          message: `Preparing ${asset.name} ${fileType}...`,
        });

        if (isSafari) {
          const generateFilecutPromise = generateFilecut({
            inspectable,
            fileType,
            dimensions,
            dpi,
            portalId,
            isAdmin: useLibrary,
          });
          try {
            const type = fileType === "SVG" ? "text/plain" : "image/png";
            // This will be executed synchronously in the user gesture context -- a requirement for Safari
            // We're essentially starting the clipboard operation immediately while the file is still being generated
            const clipboardItem = new window.ClipboardItem({
              [type]: generateFilecutPromise
                .then(action => {
                  if (action.error) {
                    throw new Error(action.error.message || "Failed to generate file");
                  }

                  setFileBeingCopied(
                    `${asset.name}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} ${fileType}`
                  );

                  return fetch(action.response.result.url);
                })
                .then(response => {
                  // For SVG, get text content. For PNG, get blob
                  return fileType === "SVG"
                    ? response.text().then(text => new Blob([text], { type: "text/plain" }))
                    : response.blob();
                }),
            });

            // This is the critical part - calling write() synchronously within the user gesture
            navigator.clipboard
              .write([clipboardItem])
              .then(() => {
                setCopyStatus(prev => ({ ...prev, [source]: { fileType, status: "success" } }));
                setTimeout(() => {
                  setCopyStatus(prev => ({ ...prev, [source]: { status: null } }));
                }, 3000);

                showNotification({
                  message: `${asset.name}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} ${fileType} has been copied to your clipboard`,
                  level: "info",
                });
              })
              .catch(error => {
                console.error("Safari clipboard error:", error);
                setCopyStatus(prev => ({ ...prev, [source]: { status: "error" } }));
                setTimeout(() => {
                  setCopyStatus(prev => ({ ...prev, [source]: { status: null } }));
                }, 3000);

                showNotification({
                  message: `There was a problem copying "${asset.name}" to your clipboard`,
                  level: "error",
                });
              });

            const action = await generateFilecutPromise;

            // Handle errors from file generation
            if (action.error) {
              const { message } = action.error || {};
              setExportMenuOpen(false);

              if (action.error.code === ErrorCode.downloadRequiresIdentity) {
                return showModal(ModalTypes.INFO_PROMPT, {
                  callback: () =>
                    copyAsset(source, fileType, dimensions, asset, dpi, originalEvent),
                });
              }
              if (action.error.code === ErrorCode.downloadRequestPending) {
                return showModal(ModalTypes.CONFIRMATION, {
                  title: "Request Pending",
                  message,
                  buttonText: "Continue",
                  buttonProcessingText: "Continue",
                  onConfirm: dismissModal,
                });
              }
              if (action.error.code === ErrorCode.downloadRequiresVerifiedIdentity) {
                return showModal(ModalTypes.CONFIRMATION, {
                  title: "Request Approved",
                  message,
                  buttonText: "Continue",
                  buttonProcessingText: "Continue",
                  onConfirm: dismissModal,
                });
              }
              if (action.error.code === ErrorCode.downloadRequestDenied) {
                return showModal(ModalTypes.CONFIRMATION, {
                  title: "Request Denied",
                  message,
                  buttonText: "Continue",
                  buttonProcessingText: "Continue",
                  onConfirm: dismissModal,
                });
              }
            }
            return;
          } catch (safariError) {
            console.error("Safari clipboard attempt failed:", safariError);
          }
        }

        const action = await generateFilecut({
          inspectable,
          fileType,
          dimensions,
          dpi,
          portalId,
          isAdmin: useLibrary,
        });

        if (action.error) {
          const { message } = action.error || {};
          setExportMenuOpen(false);
          // Error handling
          if (action.error.code === ErrorCode.downloadRequiresIdentity) {
            return showModal(ModalTypes.INFO_PROMPT, {
              callback: () => copyAsset(source, fileType, dimensions, asset, dpi, e),
            });
          }
          if (action.error.code === ErrorCode.downloadRequestPending) {
            return showModal(ModalTypes.CONFIRMATION, {
              title: "Request Pending",
              message,
              buttonText: "Continue",
              buttonProcessingText: "Continue",
              onConfirm: dismissModal,
            });
          }
          if (action.error.code === ErrorCode.downloadRequiresVerifiedIdentity) {
            return showModal(ModalTypes.CONFIRMATION, {
              title: "Request Approved",
              message,
              buttonText: "Continue",
              buttonProcessingText: "Continue",
              onConfirm: dismissModal,
            });
          }
          if (action.error.code === ErrorCode.downloadRequestDenied) {
            return showModal(ModalTypes.CONFIRMATION, {
              title: "Request Denied",
              message,
              buttonText: "Continue",
              buttonProcessingText: "Continue",
              onConfirm: dismissModal,
            });
          }
          showNotification({
            message:
              message ||
              "Unfortunately, there was a problem copying the asset. Please try again later.",
            level: "error",
          });

          setCopyStatus(prev => ({ ...prev, [source]: { status: "error" } }));
          setTimeout(() => {
            setCopyStatus(prev => ({ ...prev, [source]: { status: null } }));
          }, 3000);

          return;
        }

        const fileUrl = action.response.result.url;
        const response = await fetch(fileUrl);
        setFileBeingCopied(
          `${asset.name}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} ${fileType}`
        );

        if (fileType === "SVG") {
          const svgText = await response.text();
          setAssetCopyData(svgText);
          return;
        }

        // For non-Safari browsers, use the standard approach
        const blob = await (await fetch(fileUrl)).blob();
        const item = new window.ClipboardItem({
          ["image/png"]: blob,
        });

        await navigator.clipboard.write([item]);

        setCopyStatus(prev => ({ ...prev, [source]: { fileType, status: "success" } }));
        setTimeout(() => {
          setCopyStatus(prev => ({ ...prev, [source]: { status: null } }));
        }, 3000);
        showNotification({
          message: `${asset.name}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} ${fileType} has been copied to your clipboard`,
          level: "info",
        });
      } catch (err) {
        console.error("Clipboard operation failed:", err);

        showNotification({
          message: `There was a problem copying "${asset.name}" to your clipboard`,
          level: "error",
        });

        setCopyStatus(prev => ({ ...prev, [source]: { status: "error" } }));
        setTimeout(() => {
          setCopyStatus(prev => ({ ...prev, [source]: { status: null } }));
        }, 3000);
      }
    },
    [
      generateFilecut,
      inspectable,
      portalId,
      showNotification,
      space?.access?.permissions,
      dismissModal,
      showModal,
      isSafari,
    ]
  );

  const openManageModal = useCallback(() => {
    setExportMenuOpen(false);
    showModal(ModalTypes.MANAGE_FILECUTS, { inspectable });
  }, [inspectable, showModal]);

  const renderExportOptions = React.useMemo(() => {
    const { meta } = asset;
    const fromSketch = AssetType.sketchTypes.has(asset.type);

    const presetOptions =
      meta.filecuts &&
      meta.filecuts.presets.map((preset, i) => (
        <PresetExportOption
          asset={asset}
          type={preset.type}
          description={preset.description}
          size={preset.size}
          dpi={preset.dpi}
          downloadAsset={downloadAsset}
          copyAsset={copyAsset}
          key={i}
          data-testid={`dl-btn-${i}`}
          copyStatus={copyStatus.preset}
          downloadStatus={downloadStatuses.preset}
        />
      ));

    return (
      <Fragment>
        {fromSketch && <SketchTitle>Sketch data unavailable to download from the web</SketchTitle>}
        {presetOptions}
        <CustomExportOption
          asset={asset}
          downloadAsset={downloadAsset}
          copyAsset={copyAsset}
          copyStatus={copyStatus.custom}
          downloadStatus={downloadStatuses.custom}
        />
      </Fragment>
    );
  }, [asset, copyAsset, downloadAsset, copyStatus, downloadStatuses]);

  const closeMenu = useCallback(() => setExportMenuOpen(false), []);
  return (
    <Fragment>
      {children({
        buttonProps: {
          "data-popup-source": exportMenuOpen && "inspector-asset-download",
          onClick: () => (noCuts ? downloadAsset(null, asset.type, "1x") : setExportMenuOpen(true)),
        },
        noCuts,
        canManageCuts,
      })}

      {exportMenuOpen && !noCuts && (
        <ExportMenu vPos={popupVPos} hPos={popupHPos} close={closeMenu} stayOpenOnClick>
          <ExportTitle>
            <Text font="ui.smallBold">Download as</Text>
            {canManageCuts && (
              <Button
                text="Manage"
                buttonStyle="tertiary"
                size="small"
                data-test="open-filecut-manager"
                onClick={openManageModal}
              />
            )}
          </ExportTitle>
          {renderExportOptions}
        </ExportMenu>
      )}
    </Fragment>
  );
};

export default AssetDownloadPopup;
